<template>
  <div class="CommuneSearch w-full">
    <Combobox :model-value="comboboxValue" @update:model-value="handleSelect" as="div" class="relative">
      <div class="relative" @click="openDropdownOnClick" @focusin="onFocusIn" @focusout="handleFocusOut">
        <div
          :class="
            shellClass
              ? shellClass
              : ['CommuneSearch__button', { 'CommuneSearch__button--open': isFocused }]
          "
        >
          <slot v-if="shellClass" name="prependInner" />
          <ComboboxInput
            :display-value="(item: unknown) => getDisplayValue(item as GeoData | CommuneApiResponse | null)"
            :placeholder="displayPlaceholder"
            autocomplete="off"
            :disabled="isSelectionLocked"
            :class="comboboxInputClass"
            @change="onQueryChange"
          />
          <template v-if="shellClass">
            <div
              class="pointer-events-auto absolute inset-y-0 right-0 z-[1] flex items-center gap-1 pr-2"
            >
              <button
                v-if="geoData && !isSelectionLocked"
                type="button"
                :class="hostChrome.clearButtonClass"
                :aria-label="$t('myMap.communeSearch.clear', 'Effacer la sélection')"
                @click.stop="clearSelection"
              >
                <Icon icon="heroicons:x-mark-20-solid" class="h-4 w-4" aria-hidden="true" />
              </button>
              <slot name="appendInner" :is-loading="isLoading" />
              <span :class="hostChrome.chevronWrapClass" aria-hidden="true">
                <Icon icon="heroicons:chevron-up-down-20-solid" class="h-5 w-5" />
              </span>
            </div>
          </template>
          <template v-else>
            <button
              v-if="geoData && !isSelectionLocked"
              class="CommuneSearch__clearButton"
              type="button"
              :aria-label="$t('myMap.communeSearch.clear', 'Effacer la sélection')"
              @click.stop="clearSelection"
            >
              <Icon icon="heroicons:x-mark-20-solid" class="h-4 w-4" aria-hidden="true" />
            </button>
            <span class="CommuneSearch__icon">
              <Icon icon="heroicons:chevron-up-down-20-solid" class="h-5 w-5" aria-hidden="true" />
            </span>
          </template>
        </div>

        <ComboboxOptions
          static
          v-show="isFocused && !isSelectionLocked"
          :class="['CommuneSearch__options', optionsClass]"
        >
            <!-- Search mode: typed query -->
            <template v-if="searchQuery.length >= 2">
              <div
                v-if="isLoading"
                class="CommuneSearch__loading"
              >
                <span class="CommuneSearch__loadingText">{{ $t('myMap.communeSearch.searching', 'Recherche...') }}</span>
              </div>
              <div
                v-else-if="communeResults.length === 0"
                class="CommuneSearch__empty"
              >
                {{ emptyResultsLabel }}
              </div>
              <ComboboxOption
                v-for="(commune, index) in communeResults"
                :key="commune.cmnCode ?? commune.id ?? index"
                :value="commune"
                as="template"
                v-slot="{ active, selected }"
              >
                <li
                  :class="[
                    'CommuneSearch__ville-option',
                    {
                      'CommuneSearch__ville-option--active': active,
                      'CommuneSearch__ville-option--selected': selected,
                    },
                  ]"
                >
                  <Icon icon="heroicons:map-pin-20-solid" class="CommuneSearch__ville-icon" />
                  <span class="CommuneSearch__label-small">VILLE</span>
                  <span class="CommuneSearch__ville-name">{{ commune.cmnTitre ?? '' }}</span>
                  <span v-if="commune.cmnDept || commune.cmnRegion" class="CommuneSearch__ville-context">
                    {{ [commune.cmnDept, commune.cmnRegion].filter(Boolean).join(' · ') }}
                  </span>
                  <span v-if="selected" class="CommuneSearch__check-icon">
                    <Icon icon="heroicons:check-circle-20-solid" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </template>
            <!-- Browse mode: full tree region > département > ville -->
            <template v-else>
              <div v-if="isBrowseLoading" class="CommuneSearch__loading">
                <span class="CommuneSearch__loadingText">{{ $t('myMap.communeSearch.searching', 'Chargement...') }}</span>
              </div>
              <template v-else v-for="group in groupedData" :key="group.region">
                <li class="CommuneSearch__region-header">
                  <Icon icon="heroicons:map-20-solid" class="CommuneSearch__region-icon" />
                  <span class="CommuneSearch__label">REGION</span>
                  <span class="CommuneSearch__value">{{ group.region }}</span>
                </li>
                <template v-for="dept in group.departements" :key="dept.name">
                  <li class="CommuneSearch__departement-header">
                    <Icon
                      icon="heroicons:building-office-20-solid"
                      class="CommuneSearch__departement-icon"
                    />
                    <span class="CommuneSearch__label">{{ $t('myMap.communeSearch.department') }}</span>
                    <span class="CommuneSearch__value">{{ dept.name }}</span>
                  </li>
                  <ComboboxOption
                    v-for="commune in dept.communes"
                    :key="commune.cmnCode ?? commune.id"
                    :value="commune"
                    as="template"
                    v-slot="{ active, selected }"
                  >
                    <li
                      :class="[
                        'CommuneSearch__ville-option',
                        {
                          'CommuneSearch__ville-option--active': active,
                          'CommuneSearch__ville-option--selected': selected,
                        },
                      ]"
                    >
                      <Icon icon="heroicons:map-pin-20-solid" class="CommuneSearch__ville-icon" />
                      <span class="CommuneSearch__label-small">VILLE</span>
                      <span class="CommuneSearch__ville-name">{{ commune.cmnTitre ?? '' }}</span>
                      <span v-if="selected" class="CommuneSearch__check-icon">
                        <Icon icon="heroicons:check-circle-20-solid" aria-hidden="true" />
                      </span>
                    </li>
                  </ComboboxOption>
                </template>
              </template>
            </template>
          </ComboboxOptions>
      </div>
    </Combobox>
  </div>
</template>

<script setup lang="ts">
import { filterAllowedCommunes } from '@/constants/activeCommunes'
import type { CommuneApiResponse } from '@/services/commune/CommuneService'
import { CommuneService } from '@/services/commune/CommuneService'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import { debounce } from '@/services/utils/UtilsService'
import { navigateToCommuneGeoportail } from '@/services/commune/navigateGeoportailCommune'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

interface BrowseGroup {
  region: string
  departements: { name: string; communes: CommuneApiResponse[] }[]
}

const props = withDefaults(
  defineProps<{
    selectionBehavior?: 'bindModel' | 'navigate'
    /** Tailwind (or other) classes for the field shell — preserves original Header / Autocomplete look. */
    shellClass?: string
    /** Classes for the single ComboboxInput when shellClass is set. */
    inputClass?: string
    /** Muted chrome for clear/chevron when using shellClass. */
    chromeTone?: 'onDark' | 'onLight'
    /** Extra classes on the dropdown panel (e.g. z-index above the app header). */
    optionsClass?: string
    placeholderOverride?: string
    /** Overrides i18n when search returns zero communes (host-specific copy). */
    noResultsMessage?: string
    /** When true, selection is locked to current commune and the control is read-only. */
    disabled?: boolean
  }>(),
  {
    selectionBehavior: 'bindModel',
    chromeTone: 'onLight',
    disabled: false,
  },
)

const geoData = defineModel<GeoData | null>({
  default: null,
})

const emit = defineEmits<{
  communeSelected: [commune: CommuneApiResponse]
}>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const isSelectionLocked = computed(() => props.disabled)

const displayPlaceholder = computed(
  () => props.placeholderOverride ?? t('myMap.communeSearch.placeholder'),
)

const emptyResultsLabel = computed(
  () => props.noResultsMessage ?? t('myMap.communeSearch.noData', 'Aucune commune trouvée'),
)

const comboboxInputClass = computed(() => {
  if (props.shellClass) {
    return (
      props.inputClass ??
      'min-w-[50px] flex-1 border-0 bg-transparent text-sm focus:outline-none focus:ring-0'
    )
  }
  return 'CommuneSearch__input'
})

const hostChrome = computed(() => {
  if (props.chromeTone === 'onDark') {
    return {
      clearButtonClass:
        'flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-white/80 hover:bg-white/10 hover:text-white',
      chevronWrapClass: 'flex shrink-0 items-center text-white/80',
    }
  }
  return {
    clearButtonClass:
      'flex shrink-0 items-center justify-center rounded border-0 bg-transparent p-0.5 text-gray-400 hover:text-gray-600',
    chevronWrapClass: 'flex shrink-0 items-center text-gray-400',
  }
})

const searchQuery = ref('')
const communeResults = ref<CommuneApiResponse[]>([])
const allCommunes = ref<CommuneApiResponse[]>([])
const isLoading = ref(false)
const isBrowseLoading = ref(false)
const isFocused = ref(false)
const selectedItem = ref<CommuneApiResponse | null>(null)

const comboboxValue = computed(() => selectedItem.value ?? geoData.value)

const groupedData = computed<BrowseGroup[]>(() => {
  const regionMap = new Map<string, Map<string, CommuneApiResponse[]>>()
  for (const c of allCommunes.value) {
    const region = c.cmnRegion ?? 'Inconnue'
    const dept = c.cmnDept ?? 'Inconnu'
    if (!regionMap.has(region)) regionMap.set(region, new Map())
    const deptMap = regionMap.get(region)!
    if (!deptMap.has(dept)) deptMap.set(dept, [])
    deptMap.get(dept)!.push(c)
  }
  return Array.from(regionMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, deptMap]) => ({
      region,
      departements: Array.from(deptMap.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([name, communes]) => ({
          name,
          communes: communes.sort((a, b) =>
            (a.cmnTitre ?? '').localeCompare(b.cmnTitre ?? ''),
          ),
        })),
    }))
})

onMounted(async () => {
  isBrowseLoading.value = true
  allCommunes.value = filterAllowedCommunes(await CommuneService.getAllCommunes())
  isBrowseLoading.value = false
})

function handleFocusOut(event: FocusEvent) {
  const container = event.currentTarget as HTMLElement | null
  const related = event.relatedTarget as Node | null
  setTimeout(() => {
    if (!container) return
    if (related && container.contains(related)) return
    if (document.activeElement && container.contains(document.activeElement)) return
    isFocused.value = false
  }, 0)
}

function onFocusIn() {
  if (isSelectionLocked.value) return
  isFocused.value = true
}

function openDropdownOnClick(event: MouseEvent): void {
  if (isSelectionLocked.value) return
  isFocused.value = true
  const container = event.currentTarget as HTMLElement | null
  const input = container?.querySelector('input')
  input?.focus()
}

const performSearch = debounce(async () => {
  const q = searchQuery.value?.trim()
  if (!q || q.length < 2) {
    communeResults.value = []
    isLoading.value = false
    return
  }
  communeResults.value = filterAllowedCommunes(await CommuneService.searchCommunes(q, 15))
  isLoading.value = false
}, 300)

function onQueryChange(event: Event) {
  if (isSelectionLocked.value) return
  const target = event.target as HTMLInputElement
  searchQuery.value = target?.value ?? ''
  if (searchQuery.value?.trim().length >= 2) {
    isLoading.value = true
    performSearch()
  } else {
    communeResults.value = []
  }
}

function communeToGeoData(commune: CommuneApiResponse): GeoData {
  const lng = commune.cmnLong
  const lat = commune.cmnLat
  return {
    name: commune.cmnTitre ?? null,
    osmId: null,
    osmType: null,
    latitude: typeof lat === 'number' ? lat : undefined,
    longitude: typeof lng === 'number' ? lng : undefined,
    coords: typeof lng === 'number' && typeof lat === 'number' ? { lng, lat } : undefined,
  }
}

function getDisplayValue(item: GeoData | CommuneApiResponse | null): string {
  if (!item) return ''
  if ('cmnTitre' in item) {
    const c = item as CommuneApiResponse
    const parts = [c.cmnRegion, c.cmnDept, c.cmnTitre].filter(Boolean)
    return parts.length > 1 ? parts.join(' > ') : (c.cmnTitre ?? '')
  }
  return (item as GeoData).name ?? ''
}

function handleSelect(item: CommuneApiResponse | GeoData | null) {
  if (isSelectionLocked.value) return
  if (!item) {
    geoData.value = null
    selectedItem.value = null
    return
  }
  if ('cmnTitre' in item) {
    const commune = item as CommuneApiResponse
    if (props.selectionBehavior === 'navigate') {
      const title = commune.cmnTitre ?? ''
      navigateToCommuneGeoportail(title, router, route)
      selectedItem.value = null
      geoData.value = null
      searchQuery.value = ''
      communeResults.value = []
      isFocused.value = false
      return
    }
    selectedItem.value = commune
    geoData.value = communeToGeoData(commune)
    emit('communeSelected', commune)
  } else {
    selectedItem.value = null
    geoData.value = item as GeoData
  }
  searchQuery.value = ''
  communeResults.value = []
  isFocused.value = false
}

function clearSelection() {
  if (isSelectionLocked.value) return
  geoData.value = null
  selectedItem.value = null
  searchQuery.value = ''
  communeResults.value = []
}

watch(
  () => geoData.value,
  (val) => {
    if (!val) {
      searchQuery.value = ''
      communeResults.value = []
      selectedItem.value = null
      return
    }
    const match = allCommunes.value.find((c) => c.cmnTitre === val.name)
    selectedItem.value = match ?? null
  },
)
</script>

<style lang="scss">
.CommuneSearch {
  width: 100%;

  &__button {
    position: relative;
    width: 100%;
    cursor: text;
    border-radius: var(--dim-radius);
    border: 1px solid var(--neutral-border);
    background: rgb(var(--v-theme-surface));
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    text-align: left;
    font-size: 0.875rem;
    min-height: 2.5rem;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      border-color: rgba(0, 0, 0, 0.15);
    }

    &--open:focus-within {
      outline: none;
      border-color: rgb(var(--v-theme-main-blue));
      box-shadow: 0 0 0 2px rgba(var(--v-theme-main-blue), 0.12);
    }
  }

  &__input {
    width: 100%;
    border: none;
    background: transparent;
    font: inherit;
    color: inherit;
    outline: none;

    &::placeholder {
      color: rgb(156, 163, 175);
      font-style: italic;
    }
  }

  &__clearButton {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: var(--dim-radius);
    color: rgb(156, 163, 175);
    transition: all 0.2s ease-in-out;
    z-index: 1;

    &:hover {
      color: rgb(239, 68, 68);
      background: rgba(239, 68, 68, 0.1);
    }

    &:focus {
      outline: none;
      color: rgb(239, 68, 68);
      background: rgba(239, 68, 68, 0.1);
    }
  }

  &__icon {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    padding-right: 0.5rem;
    pointer-events: none;
    color: rgb(156, 163, 175);
    transition: color 0.2s ease-in-out;

    .CommuneSearch__button:hover & {
      color: rgb(107, 114, 128);
    }
  }

  &__options {
    position: absolute;
    z-index: 10;
    margin-top: 0.25rem;
    max-height: 20rem;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: var(--dim-radius);
    background: rgb(var(--v-theme-surface));
    padding: 0.5rem 0;
    font-size: 0.875rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid var(--neutral-border);
    outline: none;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgb(var(--v-theme-background));
      border-radius: var(--dim-radius);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: var(--dim-radius);

      &:hover {
        background: rgb(156, 163, 175);
      }
    }
  }

  &__loading,
  &__empty {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: rgb(107, 114, 128);
  }

  &__loadingText {
    font-style: italic;
  }

  &__region-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(75, 85, 99);
    background: linear-gradient(to right, rgb(249, 250, 251), rgb(243, 244, 246));
    border-bottom: 1px solid rgb(229, 231, 235);
    margin-top: 0.25rem;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__region-icon {
    width: 1rem;
    height: 1rem;
    color: rgb(var(--v-theme-main-blue));
    flex-shrink: 0;
  }

  &__departement-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 1rem 0.5rem 2rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: rgb(107, 114, 128);
    background: rgb(250, 250, 250);
    border-left: 3px solid rgb(var(--v-theme-main-blue));
    margin-top: 0.25rem;
    position: sticky;
    top: 2.5rem;
    z-index: 1;
  }

  &__departement-icon {
    width: 0.875rem;
    height: 0.875rem;
    color: rgb(107, 114, 128);
    flex-shrink: 0;
  }

  &__value {
    flex: 1;
    font-weight: 600;
  }

  &__label {
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    color: rgb(var(--v-theme-main-blue));
    background: rgba(var(--v-theme-main-blue), 0.1);
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    flex-shrink: 0;
  }

  &__label-small {
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: rgb(var(--v-theme-main-blue));
    background: rgba(var(--v-theme-main-blue), 0.08);
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    flex-shrink: 0;
    margin-right: 0.5rem;
  }

  &__ville-option {
    position: relative;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    cursor: pointer;
    user-select: none;
    padding: 0.625rem 1rem 0.625rem 3rem;
    color: rgb(17, 24, 39);
    transition: all 0.15s ease-in-out;

    .CommuneSearch__label-small {
      margin-left: 0.5rem;
    }

    &--active {
      background: rgba(var(--v-theme-main-blue), 0.08);
      color: rgb(var(--v-theme-main-blue));
    }

    &--selected {
      background: rgba(var(--v-theme-main-blue), 0.12);
      color: rgb(var(--v-theme-main-blue));
      font-weight: 600;
    }

    &:hover {
      background: rgba(var(--v-theme-main-blue), 0.05);
    }
  }

  &__ville-icon {
    position: absolute;
    left: 1rem;
    width: 1rem;
    height: 1rem;
    color: rgb(156, 163, 175);
    flex-shrink: 0;
    transition: color 0.15s ease-in-out;

    .CommuneSearch__ville-option--active &,
    .CommuneSearch__ville-option--selected & {
      color: rgb(var(--v-theme-main-blue));
    }
  }

  &__ville-name {
    flex: 1;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__ville-context {
    width: 100%;
    font-size: 0.75rem;
    color: rgb(107, 114, 128);
    padding-left: 2.5rem;

    .CommuneSearch__ville-option--active &,
    .CommuneSearch__ville-option--selected & {
      color: rgb(var(--v-theme-main-blue));
      opacity: 0.9;
    }
  }

  &__check-icon {
    display: flex;
    align-items: center;
    color: rgb(var(--v-theme-main-blue));
    margin-left: auto;

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

}
</style>
